import React from 'react';
import { Colors, FontSize, FontWeight, LineHeight } from '@common/tokens';
import { Col, Row } from 'antd';
import { ContentContainer, TextArea, Collapse } from '@common/components';
import {
  OrganizationBusinessType,
  OrganizationUnitType,
} from '@institutions/graphql';
import { SupplierType } from '@institutions/types';

function groupByOrganizationBusiness(
  organizationUnits: OrganizationUnitType[]
) {
  const organizationBusinesses: Record<
    string,
    { business: OrganizationBusinessType; units: OrganizationUnitType[] }
  > = {};
  organizationUnits.forEach((unit) => {
    const { organizationBusiness } = unit.department;

    if (!organizationBusinesses[organizationBusiness.id]) {
      organizationBusinesses[organizationBusiness.id] = {
        business: organizationBusiness,
        units: [],
      };
    }
    organizationBusinesses[organizationBusiness.id].units.push(unit);
  });

  return organizationBusinesses;
}

export function SuppliersManagementBulkLocationsUpdateStepReview({
  selectedSuppliersIds,
  suppliers,
  selectedUnitsIds,
  organizationUnits,
  onNotesChange,
}: {
  selectedSuppliersIds: string[];
  suppliers: SupplierType[];
  selectedUnitsIds: string[];
  organizationUnits: OrganizationUnitType[];
  onNotesChange: (notes: string) => void;
}) {
  const selectedOrganizationUnits: OrganizationUnitType[] = [];
  const selectedSuppliers: SupplierType[] = [];

  organizationUnits.forEach((unit) => {
    if (selectedUnitsIds.includes(unit.id))
      selectedOrganizationUnits.push(unit);
  });

  suppliers.forEach((supplier) => {
    if (selectedSuppliersIds.includes(supplier.id))
      selectedSuppliers.push(supplier);
  });

  const organizationBusinessesFromAddedUnits = Object.values(
    groupByOrganizationBusiness(selectedOrganizationUnits)
  );

  return (
    <>
      <div className="bulk-supplier-management-locations-update-step-review">
        <ContentContainer title="Update Review">
          <Row>
            <Col span={12}>
              {organizationBusinessesFromAddedUnits.length > 0 && (
                <section className="bulk-supplier-management-locations-update-step-review__added-units">
                  <h4 className="bulk-supplier-management-locations-update-step-review__section-title">
                    Permitted Units ({selectedUnitsIds.length}) Added
                  </h4>
                  <Collapse accordion>
                    {organizationBusinessesFromAddedUnits.map(
                      ({ business, units }) => (
                        <Collapse.Panel
                          key={business.id}
                          className="bulk-supplier-management-locations-update-step-review__collapse-item"
                          header={
                            <label className="bulk-supplier-management-locations-update-step-review__org-business-title">
                              {business.name} ({units.length})
                            </label>
                          }
                        >
                          <ul>
                            {units.map((unit) => (
                              <li key={unit.id}>
                                {unit.name}, {unit.clinicalUnit.displayName}{' '}
                                {unit.costCenter}
                              </li>
                            ))}
                          </ul>
                        </Collapse.Panel>
                      )
                    )}
                  </Collapse>
                </section>
              )}
            </Col>
            <Col span={12}>
              <h4 className="bulk-supplier-management-locations-update-step-review__section-title">
                Suppliers
              </h4>
              <ul>
                {selectedSuppliers.map((supplier) => (
                  <li key={supplier.id}>{supplier.name}</li>
                ))}
              </ul>
              <section className="bulk-supplier-management-locations-update-step-review__note-box">
                <p className="bulk-supplier-management-locations-update-step-review__note-label">
                  <span>*</span> Note
                </p>
                <TextArea
                  autoFocus
                  placeholder="Add a note for this supplier"
                  autoSize={{ minRows: 5 }}
                  onChange={(e) => onNotesChange(e.target.value.trim())}
                />
              </section>
            </Col>
          </Row>
        </ContentContainer>
      </div>
      <style jsx>{`
        .bulk-supplier-management-locations-update-step-review__section-title {
          font-size: ${FontSize.xl};
          line-height: 27px;
          font-weight: 900;
          margin-bottom: 17px;
        }

        .bulk-supplier-management-locations-update-step-review__org-business-title {
          font-weight: ${FontWeight.bold};
          font-size: ${FontSize.md};
          line-height: ${LineHeight.sm};
          color: ${Colors.elm};
        }

        .bulk-supplier-management-locations-update-step-review li {
          line-height: ${LineHeight.sm};
          font-weight: 400;
          margin-bottom: 4px;
        }

        :global(
          .bulk-supplier-management-locations-update-step-review__note-box
            textarea
        ) {
          font-size: ${FontSize.md};
          line-height: ${LineHeight.sm};
          padding: 14px 12px;
        }

        .bulk-supplier-management-locations-update-step-review__note-label {
          line-height: ${LineHeight.sm};
          font-weight: 400;
          color: ${Colors.midnightDarker};
          padding-top: 8px;
          margin-bottom: 8px;
        }

        .bulk-supplier-management-locations-update-step-review__note-label
          span {
          color: ${Colors.coral};
        }
      `}</style>
    </>
  );
}
